import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface Organisation {
  id: string;
  name: string;
  registrationNumber?: string;
  registrationAuthority?: string;
  businessType: string;
  status: string;
}

export interface Member {
  id: string;
  userId: string;
  orgId: string;
  roleId: string;
  roleName?: string;
  status: string;
  joinedDate: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface Invitation {
  id: string;
  orgId: string;
  inviteeEmail: string;
  invitedBy: string;
  inviteCode?: string;
  roleId: string;
  status: string;
  sentAt: string;
  expiresAt: string;
}

export interface InvitePreview {
  orgName: string;
  inviterName: string;
  roleName: string;
  inviteeEmail: string;
  status: string;
  expiresAt: string;
}

export interface Role {
  id: string;
  name: string;
  scope: string;
  isSystemRole: boolean;
  permissionsConfig: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const portalApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganisation: builder.query<Organisation, string>({
      query: (orgId) => `/portal/organisations/${orgId}`,
      transformResponse: (response: unknown) => normalizeKeys<Organisation>(response),
      providesTags: ['Organisation'],
    }),

    updateOrganisation: builder.mutation<Organisation, { orgId: string; body: Partial<Organisation> }>({
      query: ({ orgId, body }) => ({
        url: `/portal/organisations/${orgId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Organisation'],
    }),

    createOrganisation: builder.mutation<
      Organisation,
      { name: string; description?: string; businessType: string }
    >({
      query: (body) => ({
        url: '/portal/organisations',
        method: 'POST',
        body: {
          name: body.name,
          business_type: body.businessType,
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Organisation>(response),
      invalidatesTags: ['Organisation'],
    }),

    getMembers: builder.query<PaginatedResponse<Member>, { orgId: string; page?: number; pageSize?: number }>({
      query: ({ orgId, page = 1, pageSize = 20 }) =>
        `/portal/organisations/${orgId}/members?page=${page}&page_size=${pageSize}`,
      transformResponse: (response: unknown) => normalizeKeys<PaginatedResponse<Member>>(response),
      providesTags: ['Member'],
    }),

    inviteMember: builder.mutation<
      Invitation,
      { orgId: string; email: string; roleId: string; sendEmail?: boolean }
    >({
      query: ({ orgId, email, roleId, sendEmail = true }) => ({
        url: `/portal/organisations/${orgId}/members`,
        method: 'POST',
        body: { email, role_id: roleId, send_email: sendEmail },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Invitation>(response),
      invalidatesTags: ['Member', 'Invitation'],
    }),

    updateMemberRole: builder.mutation<Member, { memberId: string; roleId: string }>({
      query: ({ memberId, ...body }) => ({
        url: `/portal/members/${memberId}/role`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Member'],
    }),

    removeMember: builder.mutation<void, string>({
      query: (memberId) => ({
        url: `/portal/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Member'],
    }),

    getPendingInvitations: builder.query<Invitation[], string>({
      query: (orgId) => `/portal/organisations/${orgId}/invitations`,
      transformResponse: (response: unknown) => normalizeKeys<Invitation[]>(response),
      providesTags: ['Invitation'],
    }),

    cancelInvitation: builder.mutation<void, string>({
      query: (invitationId) => ({
        url: `/portal/invitations/${invitationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invitation'],
    }),

    resendInvitation: builder.mutation<Invitation, string>({
      query: (invitationId) => ({
        url: `/portal/invitations/${invitationId}/resend`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) => normalizeKeys<Invitation>(response),
      invalidatesTags: ['Invitation'],
    }),

    acceptInvitation: builder.mutation<Member, string>({
      query: (inviteCode) => ({
        url: '/portal/invitations/accept',
        method: 'POST',
        body: { invite_code: inviteCode },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Member>(response),
      invalidatesTags: ['Member', 'Organisation', 'Invitation'],
    }),

    getInvitePreview: builder.query<InvitePreview, string>({
      query: (inviteCode) => `/public/invitations/${encodeURIComponent(inviteCode)}`,
      transformResponse: (response: unknown) => normalizeKeys<InvitePreview>(response),
    }),

    getRoles: builder.query<Role[], string>({
      query: (orgId) => `/portal/organisations/${orgId}/roles`,
      transformResponse: (response: unknown) => normalizeKeys<Role[]>(response),
      providesTags: ['Role'],
    }),
  }),
});

export const {
  useGetOrganisationQuery,
  useUpdateOrganisationMutation,
  useCreateOrganisationMutation,
  useGetMembersQuery,
  useInviteMemberMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useGetPendingInvitationsQuery,
  useCancelInvitationMutation,
  useResendInvitationMutation,
  useAcceptInvitationMutation,
  useGetInvitePreviewQuery,
  useGetRolesQuery,
} = portalApi;
